import React from 'react';
import { motion } from 'framer-motion';

const projects = [
  {
    image: "/referenz-1.jpeg",
    alt: "Beleuchtungsinstallation – Strahlerleiste an der Decke im Sitzungssaal einer Kirchengemeinde",
    title: 'Beleuchtungsinstallation',
    description: 'Professionelle Beleuchtungslösung für ein modernes Wohn- und Gewerbeprojekt.',
    tag: 'Beleuchtung',
    objectPosition: 'center top',
  },
  {
    image: "/referenz-2.jpeg",
    alt: "Individuelle Lichtplanung und Umsetzung für Wohnräume",
    title: 'Lichtplanung & Umsetzung',
    description: 'Individuelle Lichtkonzepte für Wohnräume – von der Planung bis zur Montage.',
    tag: 'Beleuchtung',
    objectPosition: 'center',
  },
  {
    image: "/referenz-neubau.jpeg",
    alt: "Neubauprojekt – moderne Architektur mit Elektroinstallation von Daniels Elektroinstallationen (Foto: Stefan Müller, Berlin / Uwe Schröder Architekt)",
    title: 'Elektroinstallation Neubau',
    description: 'Komplette Elektroplanung und Installation für ein modernes Neubauprojekt in Bonn.',
    tag: 'Neubau',
    objectPosition: 'center',
    credit: 'Foto: Stefan Müller, Berlin · Urheber Neubau: Uwe Schröder Architekt (usarch.de, baukunst-nrw.de)',
  },
  {
    image: "/referenz-sprechanlage.jpg",
    alt: "Video-Audio-Sprechanlage mit Videokamera, Zugangskontrolle und Infomodulen von Daniels Elektroinstallationen",
    title: 'Sprechanlagen – Video/Audio/Zugangskontrolle',
    description: 'Realisierung und Erweiterung einer Video-Audio-Sprechanlage in 2-Draht-Bus-Technologie mit Zugangskontrolle und Infomodulen.',
    tag: 'Sprechanlagen',
    objectPosition: 'center',
  },
];

export default function ReferenzenSection() {
  return (
    <section id="referenzen" data-testid="referenzen-section" className="py-24 sm:py-32 bg-[#F4F8F4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="text-[#10B981] font-['Manrope'] text-sm font-semibold tracking-[0.2em] uppercase">
            Referenzen
          </span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#1A3A22] tracking-tight mt-3 mb-4">
            Unsere <span className="text-[#2D5A27]">Projekte</span>
          </h2>
          <p className="font-['Manrope'] text-gray-600 text-base sm:text-lg max-w-xl mx-auto">
            Ein Auszug aus unseren erfolgreich abgeschlossenen Projekten.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {projects.map((project, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              data-testid={`project-${idx}`}
              className="group bg-white border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-400"
            >
              {/* Bild groß oben */}
              <div className="aspect-[16/10] overflow-hidden bg-[#F4F8F4]">
                <img
                  src={project.image}
                  alt={project.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  style={{ objectPosition: project.objectPosition }}
                />
              </div>
              {/* Beschreibung darunter */}
              <div className="p-6 sm:p-7">
                <span className="inline-block bg-[#10B981] text-white text-xs font-['Manrope'] font-semibold px-3 py-1 mb-4 tracking-wider uppercase">
                  {project.tag}
                </span>
                <h3 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-[#1A3A22] mb-3 leading-tight">
                  {project.title}
                </h3>
                <p className="font-['Manrope'] text-gray-600 text-sm sm:text-base leading-relaxed">
                  {project.description}
                </p>
                {project.credit && (
                  <p className="font-['Manrope'] text-gray-400 text-xs italic mt-4 pt-3 border-t border-gray-100">
                    {project.credit}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Vorher/Nachher Sektion */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-12"
          data-testid="vorher-nachher"
        >
          <div className="text-center mb-8">
            <h3 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-[#1A3A22]">
              Vorher & Nachher: <span className="text-[#2D5A27]">Zähleranlage Mehrparteienhaus</span>
            </h3>
            <p className="font-['Manrope'] text-gray-600 text-sm sm:text-base mt-2 max-w-2xl mx-auto">
              Komplette Erneuerung einer veralteten Zähleranlage in einem Mehrparteienhaus –
              von der alten Backsteinwand mit offener Verkabelung bis zur modernen, fertiggestellten
              Anlage mit sauberen Abdeckungen.
            </p>
          </div>

          {/* Kollage-Bild: 4 Etappen in einem Bild */}
          <div
            className="relative bg-white border border-gray-100 shadow-lg overflow-hidden max-w-3xl mx-auto"
            data-testid="vorher-nachher-collage"
          >
            <img
              src="/vorher-nachher-collage.webp"
              alt="Zähleranlage Mehrparteienhaus – Vorher (Backsteinwand, alte Verkabelung) und Nachher (moderne Anlage mit Abdeckungen)"
              className="w-full h-auto object-contain"
            />
          </div>

          {/* Legende unter dem Bild */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 max-w-3xl mx-auto">
            <div className="p-3 bg-[#F4F8F4] border-l-4 border-[#2D5A27]">
              <span className="font-['Outfit'] text-xs font-semibold text-[#2D5A27] uppercase tracking-wider block mb-1">Schritt 1</span>
              <span className="font-['Manrope'] text-xs text-gray-600">Neue Anlage – offen mit Verkabelung</span>
            </div>
            <div className="p-3 bg-[#F4F8F4] border-l-4 border-[#2D5A27]">
              <span className="font-['Outfit'] text-xs font-semibold text-[#2D5A27] uppercase tracking-wider block mb-1">Schritt 2</span>
              <span className="font-['Manrope'] text-xs text-gray-600">Module montiert, Kabel verlegt</span>
            </div>
            <div className="p-3 bg-[#F4F8F4] border-l-4 border-[#10B981]">
              <span className="font-['Outfit'] text-xs font-semibold text-[#10B981] uppercase tracking-wider block mb-1">Nachher</span>
              <span className="font-['Manrope'] text-xs text-gray-600">Fertiggestellt mit Abdeckungen</span>
            </div>
            <div className="p-3 bg-[#F4F8F4] border-l-4 border-red-600">
              <span className="font-['Outfit'] text-xs font-semibold text-red-600 uppercase tracking-wider block mb-1">Vorher</span>
              <span className="font-['Manrope'] text-xs text-gray-600">Alte Backsteinwand, offene Verkabelung</span>
            </div>
          </div>

          {/* Klassische Vorher/Nachher-Gegenüberstellung – direkter Vergleich */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {/* Vorher */}
            <div className="relative" data-testid="vorher-image">
              <img
                src="/referenz-vorher.jpeg"
                alt="Alte Zähleranlage - Vorher"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute top-4 left-4 bg-red-600 text-white px-4 py-1.5">
                <span className="font-['Outfit'] font-semibold text-sm tracking-wider uppercase">Vorher</span>
              </div>
              <div className="p-4 bg-white border border-gray-100">
                <p className="font-['Manrope'] text-gray-600 text-sm">
                  <strong className="text-[#1A3A22]">Problem:</strong> Veraltete Zähleranlage, nicht mehr normgerecht, Sicherheitsrisiko für alle Bewohner.
                </p>
              </div>
            </div>

            {/* Nachher */}
            <div className="relative" data-testid="nachher-image">
              <img
                src="/referenz-nachher.jpeg"
                alt="Neue Zähleranlage - Nachher"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute top-4 left-4 bg-[#2D5A27] text-white px-4 py-1.5">
                <span className="font-['Outfit'] font-semibold text-sm tracking-wider uppercase">Nachher</span>
              </div>
              <div className="p-4 bg-white border border-gray-100">
                <p className="font-['Manrope'] text-gray-600 text-sm">
                  <strong className="text-[#1A3A22]">Lösung:</strong> Neue, normgerechte Zähleranlage – übersichtlich, sicher und zukunftsfähig für alle Parteien.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
